import useLinearLightErrorNotice from '../../utils/linearLight/useErrorNotice';
import { useCallback, useContext, useEffect } from 'react';
import { LinearLightEditorContext } from '../../utils/linearLight/editor';
import LinearLightLayer from '../../utils/linearLight/layer';
import { isLinearLightBlendMode } from '../../utils/linearLight/source';
/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useUnits } from '@web-stories-wp/units';

/**
 * Internal dependencies
 */
import { useMemo, useState, useRef } from '@web-stories-wp/react';
import TextureEdit from '../../elements/text/textureEdit';
import { useCanvas } from '../../app';
import { elementIs, getDefinitionForType } from '../../elements';
import {
  elementWithPosition,
  elementWithSize,
  elementWithRotation,
} from '../../elements/shared';
import SingleSelectionMoveable from './singleSelectionMoveable';
import useColorTransformHandler from '../../elements/shared/useColorTransformHandler';
import { TextBoxBackdropGroup } from '../../elements/text/textBoxBackdrop';
import {
  getTextBoxBackdropStyles,
  normalizeTextBoxBackdropValue,
} from '../../elements/text/getTextBoxBackdropStyles';
import {
  getElementBackdropStyles,
  hasElementBackdrop,
} from '../../elements/shared/getElementBackdropStyles';
import ElementBackdrop from '../../elements/shared/elementBackdrop';
import { getTextShadowStyles } from '../../elements/text/getTextShadowStyles';
import { getElementBlendMode } from '../../elements/shared/blendModes';
import {
  getBorderPositionCSS,
  getBorderRadius,
  getResponsiveBorder,
  shouldDisplayBorder,
} from '../../utils/elementBorder';

const Wrapper = styled.div`
  ${elementWithPosition}
  ${elementWithSize}
	${elementWithRotation}
	pointer-events: initial;
`;

const ForegroundLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
`;

function EditElementContent({ element }) {
  const { id, type } = element;
  const { getBox, dataToEditorX, dataToEditorY } = useUnits((state) => ({
    getBox: state.actions.getBox,
    dataToEditorX: state.actions.dataToEditorX,
    dataToEditorY: state.actions.dataToEditorY,
  }));

  const [editWrapper, setEditWrapper] = useState(null);
  // Needed for elements that can scale in edit mode.
  const [localProperties, setLocalProperties] = useState(null);

  const textureMode = useCanvas(
    ({ state }) =>
      state.editingElementState?.texture === true && elementIs.text(element)
  );
  const { Edit: DefaultEdit, hasEditModeMoveable: defaultMoveable } =
    getDefinitionForType(type);
  const Edit = textureMode ? TextureEdit : DefaultEdit;
  const hasEditModeMoveable = defaultMoveable && !textureMode;
  const currentElement = useMemo(
    () => (localProperties ? { ...element, ...localProperties } : element),
    [element, localProperties]
  );
  const box = getBox(currentElement);
  const responsiveBorder = getResponsiveBorder(
    currentElement.border,
    true,
    dataToEditorX
  );

  const moveable = useRef(null);
  const backdropTintRef = useRef(null);
  const elementBackdropRef = useRef(null);
  const elementBlendMode = getElementBlendMode(currentElement.blendMode);
  const textBoxBackdropStyle = useMemo(
    () =>
      elementIs.text(currentElement)
        ? getTextBoxBackdropStyles({
            element: currentElement,
            dataToStyleX: (value) => `${dataToEditorX(value)}px`,
            dataToStyleY: (value) => `${dataToEditorY(value)}px`,
            dataToBlur: (value) => `${dataToEditorX(value)}px`,
            disableBlur:
              Boolean(elementBlendMode) ||
              isLinearLightBlendMode(currentElement.blendMode),
          })
        : null,
    [currentElement, dataToEditorX, dataToEditorY, elementBlendMode]
  );
  const elementBackdropStyle = useMemo(
    () =>
      getElementBackdropStyles({
        element: currentElement,
        applyFlip: false,
        includeBorder: false,
        dataToStyleX: (value) => `${dataToEditorX(value)}px`,
        dataToStyleY: (value) => `${dataToEditorY(value)}px`,
      }),
    [currentElement, dataToEditorX, dataToEditorY]
  );
  const hasBackdrop = Boolean(textBoxBackdropStyle || elementBackdropStyle);
  const textBoxShadowStyle = elementIs.text(currentElement)
    ? getTextShadowStyles({
        element: currentElement,
        dataToStyleX: (value) => dataToEditorX(value),
        dataToStyleY: (value) => dataToEditorY(value),
      })
    : null;
  const backdropCompositeStyle = hasBackdrop
    ? {
        opacity:
          typeof currentElement.opacity !== 'undefined'
            ? currentElement.opacity / 100
            : null,
        ...(elementBlendMode ? { mixBlendMode: elementBlendMode } : null),
      }
    : null;
  const textBoxShadowLayerStyle =
    hasBackdrop && textBoxShadowStyle?.boxShadow
      ? {
          ...(shouldDisplayBorder(currentElement)
            ? getBorderPositionCSS(responsiveBorder)
            : { inset: 0 }),
          ...getBorderRadius(currentElement),
          ...backdropCompositeStyle,
          boxShadow: textBoxShadowStyle.boxShadow,
        }
      : null;

  useColorTransformHandler({
    id,
    targetRef: textBoxBackdropStyle ? backdropTintRef : null,
    expectedStyle: 'background',
    fallbackColor: currentElement.backgroundColor,
  });

  const onResize = () => {
    // Update moveable when resizing.
    if (moveable.current) {
      moveable.current.updateRect();
    }
  };

  const onCropResize = ({ width, height, x, y } = {}) => {
    const target = elementBackdropRef.current;
    if (!target || !elementBackdropStyle) return;
    const liveStyle =
      width && height
        ? getElementBackdropStyles({
            element: {
              ...currentElement,
              width: width / dataToEditorX(1),
              height: height / dataToEditorY(1),
            },
            applyFlip: false,
            includeBorder: false,
            dataToStyleX: (value) => `${dataToEditorX(value)}px`,
            dataToStyleY: (value) => `${dataToEditorY(value)}px`,
          })
        : elementBackdropStyle;
    Object.assign(target.style, liveStyle, {
      width: width
        ? `calc(${width}px + ${
            dataToEditorX(
              normalizeTextBoxBackdropValue(currentElement.backdropEdgeSoftness)
            ) * 4
          }px)`
        : elementBackdropStyle.width,
      height: height
        ? `calc(${height}px + ${
            dataToEditorY(
              normalizeTextBoxBackdropValue(currentElement.backdropEdgeSoftness)
            ) * 4
          }px)`
        : elementBackdropStyle.height,
      transform: width ? `translate(${x}px, ${y}px)` : '',
    });
  };

  const editor = (
    <Edit
      key={`${id}:${textureMode ? 'texture' : 'default'}`}
      element={currentElement}
      box={box}
      editWrapper={hasEditModeMoveable && editWrapper}
      onResize={onResize}
      setLocalProperties={setLocalProperties}
      hasElementBackdrop={Boolean(elementBackdropStyle)}
      onCropResize={elementBackdropStyle ? onCropResize : undefined}
      {...(elementIs.text(currentElement)
        ? { hasTextBoxBackdrop: hasBackdrop }
        : null)}
    />
  );

  return (
    <>
      <Wrapper aria-labelledby={`layer-${id}`} {...box} ref={setEditWrapper}>
        {textBoxBackdropStyle ? (
          <TextBoxBackdropGroup
            element={currentElement}
            style={{
              ...textBoxBackdropStyle,
              ...backdropCompositeStyle,
            }}
            shadowStyle={textBoxShadowLayerStyle}
            tintRef={backdropTintRef}
          >
            <ForegroundLayer style={backdropCompositeStyle}>
              {editor}
            </ForegroundLayer>
          </TextBoxBackdropGroup>
        ) : elementBackdropStyle ? (
          <>
            <ElementBackdrop
              backdropRef={elementBackdropRef}
              applyFlip={false}
              includeBorder={false}
              element={currentElement}
              style={elementBackdropStyle}
            />
            <ForegroundLayer style={backdropCompositeStyle}>
              {editor}
            </ForegroundLayer>
          </>
        ) : (
          editor
        )}
      </Wrapper>
      {hasEditModeMoveable && editWrapper && (
        <SingleSelectionMoveable
          selectedElement={element}
          targetEl={editWrapper}
          isEditMode
          editMoveableRef={moveable}
        />
      )}
    </>
  );
}

function EditElement(props) {
  const onError = useLinearLightErrorNotice(
    hasElementBackdrop(props.element) ? 'Background blur' : 'Linear Light'
  );
  const { element } = props;
  const { publish, clear } = useContext(LinearLightEditorContext);
  const onReady = useCallback(
    (uri, blurSource) => publish(element.id, uri, blurSource),
    [publish, element.id]
  );
  useEffect(() => () => clear(element.id), [clear, element.id]);
  if (
    (hasElementBackdrop(element) ||
      isLinearLightBlendMode(element.blendMode)) &&
    ['image', 'text', 'shape'].includes(element.type)
  ) {
    return (
      <LinearLightLayer
        backdropElementId={element.id}
        combinedBlur={hasElementBackdrop(element)}
        blendMode={element.blendMode}
        onError={onError}
        opacity={element.opacity}
        nativeEditor
        renderEffect={hasElementBackdrop(element)}
        onReady={onReady}
      >
        <EditElementContent
          {...props}
          element={{
            ...element,
            opacity: 100,
            blendMode: hasElementBackdrop(element)
              ? 'normal'
              : element.blendMode,
          }}
        />
      </LinearLightLayer>
    );
  }
  return <EditElementContent {...props} />;
}

EditElement.propTypes = {
  element: PropTypes.object.isRequired,
};

export default EditElement;
